import { useState, useMemo } from "react";
import {
  Accordion,
  Badge,
  Button,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useCostCodes, useCreateCostCode, type CostCode } from "@/hooks/useCostCodes";

interface CostCodeSelectorProps {
  opened: boolean;
  onClose: () => void;
  onSelect: (costCodeIds: string[]) => void;
  excludeIds?: string[];
  selectedIds?: string[];
  title?: string;
  confirmText?: string;
  projectId?: string; // Optional - if provided, enables creating custom codes
}

export function CostCodeSelector({
  opened,
  onClose,
  onSelect,
  excludeIds = [],
  selectedIds = [],
  title = "Select Cost Codes",
  confirmText = "Add Selected Codes",
  projectId,
}: CostCodeSelectorProps) {
  const { data, isLoading } = useCostCodes();
  const createCostCode = useCreateCostCode();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedIds));
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCode, setNewCode] = useState({ division: "", code: "", name: "" });

  // Filter out excluded IDs and apply search
  const availableCodes = useMemo(() => {
    if (!data?.costCodes) return [];

    return data.costCodes.filter((code) => {
      const isExcluded = excludeIds.includes(code.id);
      const matchesSearch =
        search === "" ||
        code.code.toLowerCase().includes(search.toLowerCase()) ||
        code.name.toLowerCase().includes(search.toLowerCase());

      return !isExcluded && matchesSearch;
    });
  }, [data?.costCodes, excludeIds, search]);

  // Group codes by division
  const groupedCodes = useMemo(() => {
    const groups: Record<string, CostCode[]> = {};

    availableCodes.forEach((code) => {
      if (!groups[code.division]) {
        groups[code.division] = [];
      }
      groups[code.division]!.push(code);
    });

    // Sort divisions by their number
    const sortedGroups: Record<string, CostCode[]> = {};
    Object.keys(groups)
      .sort((a, b) => {
        const aNum = parseInt(a.split(" ")[0] || "0");
        const bNum = parseInt(b.split(" ")[0] || "0");
        return aNum - bNum;
      })
      .forEach((key) => {
        sortedGroups[key] = groups[key]?.sort((a, b) => a.code.localeCompare(b.code)) || [];
      });

    return sortedGroups;
  }, [availableCodes]);

  const handleToggle = (codeId: string) => {
    const newSelected = new Set(selected);
    if (newSelected.has(codeId)) {
      newSelected.delete(codeId);
    } else {
      newSelected.add(codeId);
    }
    setSelected(newSelected);
  };

  const handleToggleDivision = (divisionCodes: CostCode[]) => {
    const newSelected = new Set(selected);
    const divisionCodeIds = divisionCodes.map((c) => c.id);
    const allSelected = divisionCodeIds.every((id) => newSelected.has(id));

    if (allSelected) {
      divisionCodeIds.forEach((id) => newSelected.delete(id));
    } else {
      divisionCodeIds.forEach((id) => newSelected.add(id));
    }
    setSelected(newSelected);
  };

  const handleConfirm = () => {
    onSelect(Array.from(selected));
    setSelected(new Set());
    setSearch("");
    onClose();
  };

  const handleCancel = () => {
    setSelected(new Set(selectedIds));
    setSearch("");
    setShowCreateForm(false);
    setNewCode({ division: "", code: "", name: "" });
    onClose();
  };

  const handleCreateCustomCode = async () => {
    // Validate inputs
    if (!newCode.division.trim() || !newCode.code.trim() || !newCode.name.trim()) {
      notifications.show({
        title: "Validation Error",
        message: "All fields are required to create a custom code",
        color: "red",
      });
      return;
    }

    try {
      const result = await createCostCode.mutateAsync({
        division: newCode.division.trim(),
        code: newCode.code.trim(),
        name: newCode.name.trim(),
      });

      // Auto-select the newly created code
      const newSelected = new Set(selected);
      newSelected.add(result.costCode.id);
      setSelected(newSelected);

      // Reset form
      setNewCode({ division: "", code: "", name: "" });
      setShowCreateForm(false);

      notifications.show({
        title: "Success",
        message: "Custom cost code created and selected!",
        color: "green",
      });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create custom code",
        color: "red",
      });
    }
  };

  const selectedCount = selected.size;

  return (
    <Modal
      opened={opened}
      onClose={handleCancel}
      title={title}
      size="lg"
      centered
      styles={{
        body: { maxHeight: "70vh", overflowY: "auto" },
      }}
    >
      <Stack gap="md">
        <TextInput
          placeholder="Search cost codes..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />

        {isLoading ? (
          <Text c="dimmed">Loading cost codes...</Text>
        ) : Object.keys(groupedCodes).length === 0 ? (
          <Text c="dimmed">
            {search ? "No cost codes match your search." : "No cost codes available."}
          </Text>
        ) : (
          <Accordion variant="contained" multiple>
            {Object.entries(groupedCodes).map(([division, codes]) => {
              const divisionSelectedCount = codes.filter((c) => selected.has(c.id)).length;
              const allSelected = divisionSelectedCount === codes.length;

              return (
                <Accordion.Item key={division} value={division}>
                  <Accordion.Control>
                    <Group justify="space-between">
                      <Group gap="sm">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={divisionSelectedCount > 0 && !allSelected}
                          onChange={() => handleToggleDivision(codes)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Text fw={500}>{division}</Text>
                      </Group>
                      <Badge variant="light" size="sm">
                        {divisionSelectedCount}/{codes.length}
                      </Badge>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="xs">
                      {codes.map((code) => (
                        <Checkbox
                          key={code.id}
                          checked={selected.has(code.id)}
                          onChange={() => handleToggle(code.id)}
                          label={
                            <Group gap="xs">
                              <Text size="sm" fw={500}>
                                {code.code}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {code.name}
                              </Text>
                            </Group>
                          }
                        />
                      ))}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}

        {/* Create Custom Code Section (only if projectId provided) */}
        {projectId && (
          <>
            <Divider />
            <Stack gap="sm">
              <Button
                variant="subtle"
                leftSection={<IconPlus size={16} />}
                onClick={() => setShowCreateForm(!showCreateForm)}
                fullWidth
              >
                {showCreateForm ? "Cancel Custom Code" : "Create Custom Code"}
              </Button>

              <Collapse in={showCreateForm}>
                <Stack gap="sm">
                  <TextInput
                    label="Division"
                    placeholder="e.g., 16 - Electrical"
                    value={newCode.division}
                    onChange={(e) => setNewCode({ ...newCode, division: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Code"
                    placeholder="e.g., 16 01 00"
                    value={newCode.code}
                    onChange={(e) => setNewCode({ ...newCode, code: e.currentTarget.value })}
                  />
                  <TextInput
                    label="Name"
                    placeholder="e.g., Electrical - General"
                    value={newCode.name}
                    onChange={(e) => setNewCode({ ...newCode, name: e.currentTarget.value })}
                  />
                  <Button
                    onClick={handleCreateCustomCode}
                    loading={createCostCode.isPending}
                    fullWidth
                  >
                    Create & Add to Selection
                  </Button>
                </Stack>
              </Collapse>
            </Stack>
          </>
        )}

        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            {selectedCount} {selectedCount === 1 ? "code" : "codes"} selected
          </Text>
          <Group>
            <Button variant="subtle" onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={selectedCount === 0}>
              {confirmText} ({selectedCount})
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
